import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { RestaurantService } from '../../../services/restaurant/restaurant.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IFeatureCallWaiter, IFeatureCallWaiterAction, IRestaurantInfo } from '../../../interfaces/restaurant';
import { AdminService } from '../../../services/admin/admin.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '../confirm-dialog/confirmation-dialog.component';

type CallWaiterActionKey = keyof IFeatureCallWaiter;

type TranslationForm = {
  language: FormControl<string>;
  value: FormControl<string>;
};

type CallWaiterActionForm = {
  enabled: FormControl<boolean>;
  name: FormControl<string>;
  translations: FormArray<FormGroup<TranslationForm>>;
};

type CallWaiterForm = {
  allOn: FormControl<boolean>;
  main: FormGroup<CallWaiterActionForm>;
  secondary: FormGroup<CallWaiterActionForm>;
  tertiary: FormGroup<CallWaiterActionForm>;
};

@UntilDestroy()
@Component({
  selector: 'app-call-waite-config',
  templateUrl: './call-waite-config.component.html',
  styleUrls: ['./call-waite-config.component.scss']
})
export class CallWaiteConfigComponent implements OnInit {
  private readonly actionKeys: CallWaiterActionKey[] = ['main', 'secondary', 'tertiary'];
  private readonly englishLanguage = 'en-CA';
  private readonly frenchLanguage = 'fr-CA';
  private readonly defaultTranslationLabels: {
    [actionKey in CallWaiterActionKey]: { [language: string]: string };
  } = {
    main: {
      [this.englishLanguage]: 'Call waiter',
      [this.frenchLanguage]: 'Appelle le serveur',
    },
    secondary: {
      [this.englishLanguage]: 'Request bill',
      [this.frenchLanguage]: 'Demander une facture',
    },
    tertiary: {
      [this.englishLanguage]: 'Another round',
      [this.frenchLanguage]: 'Un tour de plus',
    },
  };

  public languages = [
    { value: this.englishLanguage, viewValue: 'English' },
    { value: this.frenchLanguage, viewValue: 'French' },
  ];
  public restaurantId!: number;
  public callWaiteConfig!: IFeatureCallWaiter;
  form: FormGroup<CallWaiterForm>;
  private initialConfig!: IFeatureCallWaiter;
  private isCloseConfirmationOpened = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private restaurantService: RestaurantService,
    private adminService: AdminService,
    public dialogRef: MatDialogRef<any>,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
  ) {
    this.form = new FormGroup<CallWaiterForm>({
      allOn: new FormControl(false, { nonNullable: true }),
      main: this.generateActionGroup('callWaiter'),
      secondary: this.generateActionGroup('requestBill'),
      tertiary: this.generateActionGroup('anotherRound'),
    });
  }

  ngOnInit(): void {
    this.dialogRef.disableClose = true;
    this.restaurantId = this.data.restaurantId;
    this.adminService.getRestaurantInfo(this.restaurantId).pipe(
      map(restaurant => restaurant.features?.callWaiter),
      untilDestroyed(this)
    ).subscribe(callWaiterConfig => {
      if (typeof callWaiterConfig !== 'undefined') {
        this.applyCallWaiterConfig(callWaiterConfig);
      } else {
        this.storeInitialConfig();
      }
    });

    this.actionKeys.forEach(actionKey => this.syncActionNameControlState(actionKey));
    this.updateAllOn();

    this.form.controls.allOn.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(value => {
      this.actionKeys.forEach(actionKey => {
        this.getActionGroup(actionKey).controls.enabled.setValue(value, { emitEvent: false });
      });
      this.actionKeys.forEach(actionKey => this.syncActionNameControlState(actionKey));
    });

    this.actionKeys.forEach(actionKey => {
      this.getActionGroup(actionKey).controls.enabled.valueChanges.pipe(
        untilDestroyed(this)
      ).subscribe(() => {
        this.syncActionNameControlState(actionKey);
        this.updateAllOn();
      });
    });

    this.dialogRef.backdropClick().pipe(
      untilDestroyed(this)
    ).subscribe(() => this.onCloseClick());

    this.dialogRef.keydownEvents().pipe(
      filter(event => event.key === 'Escape'),
      untilDestroyed(this)
    ).subscribe(() => this.onCloseClick());
  }

  public onCloseClick(): void {
    if (!this.hasUnsavedChanges) {
      this.dialogRef.close();
      return;
    }

    if (this.isCloseConfirmationOpened) {
      return;
    }

    this.isCloseConfirmationOpened = true;
    const title = this.translate.instant('period.unsavedPeriodTitle');
    const message = this.translate.instant('period.unsavedChanges');
    const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message,
        title
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      untilDestroyed(this)
    ).subscribe(confirmed => {
      this.isCloseConfirmationOpened = false;

      if (confirmed) {
        this.form.markAsPristine();
        this.dialogRef.close();
      }
    });
  }

  public get hasUnsavedChanges(): boolean {
    if (!this.initialConfig) {
      return false;
    }

    return JSON.stringify(this.getCurrentConfig()) !== JSON.stringify(this.initialConfig);
  }

  public getTranslationsControls(actionKey: CallWaiterActionKey): Array<FormGroup<TranslationForm>> {
    return this.getTranslations(actionKey).controls;
  }

  public addTranslation(actionKey: CallWaiterActionKey): void {
    if (!this.canAddFrenchTranslation(actionKey)) {
      return;
    }

    this.getTranslations(actionKey).push(this.generateTranslationGroup(this.frenchLanguage));
    this.syncActionNameControlState(actionKey);
    this.form.markAsDirty();
  }

  public removeTranslation(actionKey: CallWaiterActionKey, index: number): void {
    const translations = this.getTranslations(actionKey);

    if (translations.length === 1 || index === 0) {
      return;
    }

    translations.removeAt(index);
    this.updateGeneratedName(actionKey);
    this.form.markAsDirty();
  }

  public canAddFrenchTranslation(actionKey: CallWaiterActionKey): boolean {
    return !this.getTranslationsControls(actionKey)
      .some(translation => translation.controls.language.value === this.frenchLanguage);
  }

  public getTranslationLanguageLabel(translation: FormGroup<TranslationForm>): string {
    return this.languages.find(language => language.value === translation.controls.language.value)?.viewValue || '';
  }

  public getDefaultTranslationLabel(actionKey: CallWaiterActionKey, translation: FormGroup<TranslationForm>): string {
    const language = translation.controls.language.value;

    return this.defaultTranslationLabels[actionKey][language];
  }

  updateAllOn(): void {
    const allOnValue = this.actionKeys.every(actionKey => this.getActionGroup(actionKey).controls.enabled.value);
    this.form.controls.allOn.setValue(allOnValue, { emitEvent: false });
  }

  someOn(): boolean {
    const enabledActionsCount = this.actionKeys.filter(actionKey => this.getActionGroup(actionKey).controls.enabled.value).length;
    return enabledActionsCount > 0 && enabledActionsCount < this.actionKeys.length;
  }

  private applyCallWaiterConfig(config: IFeatureCallWaiter): void {
    if (config) {
      this.callWaiteConfig = config;
    } else {
      this.callWaiteConfig = {
        main: { enabled: true, name: 'callWaiter', translations: { [this.englishLanguage]: 'Call waiter' } },
        secondary: { enabled: true, name: 'requestBill', translations: { [this.englishLanguage]: 'Request bill' } },
        tertiary: { enabled: true, name: 'anotherRound', translations: { [this.englishLanguage]: 'Another round' } },
      };
    }

    this.actionKeys.forEach(actionKey => {
      this.setActionValue(actionKey, this.callWaiteConfig[actionKey]);
      this.syncActionNameControlState(actionKey);
    });
    this.updateAllOn();
    this.storeInitialConfig();
  }

  private syncActionNameControlState(actionKey: CallWaiterActionKey): void {
    const actionGroup = this.getActionGroup(actionKey);
    const enabledControl = actionGroup.controls.enabled;
    const nameControl = actionGroup.controls.name;
    const translationsControl = this.getTranslations(actionKey);

    if (enabledControl.value) {
      translationsControl.enable({ emitEvent: false });
    } else {
      translationsControl.disable({ emitEvent: false });
    }

    nameControl.disable({ emitEvent: false });
    nameControl.updateValueAndValidity({ emitEvent: false });
    translationsControl.updateValueAndValidity({ emitEvent: false });
  }

  private setActionValue(actionKey: CallWaiterActionKey, action: IFeatureCallWaiterAction): void {
    const actionGroup = this.getActionGroup(actionKey);
    const translations = action.translations || { [this.englishLanguage]: action.name };
    const englishValue = translations[this.englishLanguage] || translations['en-CA'] || '';
    const frenchValue = translations[this.frenchLanguage];

    actionGroup.patchValue({
      enabled: action.enabled,
      name: action.name,
    }, { emitEvent: false });

    const translationGroups = [
      this.generateTranslationGroup(this.englishLanguage, englishValue),
    ];

    if (typeof frenchValue !== 'undefined') {
      translationGroups.push(this.generateTranslationGroup(this.frenchLanguage, frenchValue));
    }

    actionGroup.setControl(
      'translations',
      new FormArray<FormGroup<TranslationForm>>(translationGroups)
    );
    this.updateGeneratedName(actionKey);
  }

  private getActionGroup(actionKey: CallWaiterActionKey): FormGroup<CallWaiterActionForm> {
    return this.form.controls[actionKey];
  }

  private getTranslations(actionKey: CallWaiterActionKey): FormArray<FormGroup<TranslationForm>> {
    return this.getActionGroup(actionKey).controls.translations;
  }

  private generateActionGroup(name: string): FormGroup<CallWaiterActionForm> {
    return new FormGroup<CallWaiterActionForm>({
      enabled: new FormControl(true, { nonNullable: true }),
      name: new FormControl(name, { nonNullable: true }),
      translations: new FormArray<FormGroup<TranslationForm>>([
        this.generateTranslationGroup(this.englishLanguage)
      ]),
    });
  }

  private generateTranslationGroup(language: string, value = ''): FormGroup<TranslationForm> {
    const group = new FormGroup<TranslationForm>({
      language: new FormControl(language, { nonNullable: true, validators: [Validators.required] }),
      value: new FormControl(value, { nonNullable: true }),
    });

    group.controls.value.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      const actionKey = this.actionKeys.find(key => this.getTranslations(key).controls.includes(group));

      if (actionKey) {
        this.updateGeneratedName(actionKey);
      }
    });

    return group;
  }

  private updateGeneratedName(actionKey: CallWaiterActionKey): void {
    const firstTranslationValue = this.getTranslations(actionKey).at(0)?.controls.value.value || '';
    this.getActionGroup(actionKey).controls.name.setValue(this.toCamelCase(firstTranslationValue), { emitEvent: false });
  }

  private toCamelCase(value: string): string {
    const words = value.trim().split(/[\s_-]+/).filter(Boolean);

    if (!words.length) {
      return '';
    }

    return words
      .map((word, index) => {
        const normalizedWord = word.replace(/[^A-Za-zА-Яа-яІіЇїЄєҐґ0-9]/g, '');
        const lowerCaseWord = normalizedWord.charAt(0).toLowerCase() + normalizedWord.slice(1);

        return index === 0
          ? lowerCaseWord
          : lowerCaseWord.charAt(0).toUpperCase() + lowerCaseWord.slice(1);
      })
      .join('');
  }

  private mapActionValue(actionKey: CallWaiterActionKey): IFeatureCallWaiterAction {
    const action = this.getActionGroup(actionKey).getRawValue();
    const translations = action.translations.reduce(
      (result: { [language: string]: string }, translation: { language: string; value: string }) => ({
        ...result,
        [translation.language]: translation.value,
      }),
      {}
    );

    return {
      enabled: action.enabled,
      name: this.toCamelCase(action.translations[0]?.value || ''),
      translations,
    };
  }

  private getCurrentConfig(): IFeatureCallWaiter {
    return {
      main: this.mapActionValue('main'),
      secondary: this.mapActionValue('secondary'),
      tertiary: this.mapActionValue('tertiary'),
    };
  }

  private storeInitialConfig(): void {
    this.initialConfig = this.getCurrentConfig();
    this.form.markAsPristine();
  }

  onSubmit(): void {
    const config = this.getCurrentConfig();

    this.restaurantService.configureCollWaiter(this.restaurantId, config).subscribe((data) => {
      this.dialogRef.close(data as IRestaurantInfo);
    });
  }
}
